import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { getActorGuide } from "@/lib/charades";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;

const DIFFICULTY_LABEL = { 300: "سهل", 600: "متوسط", 900: "صعب" };

export default function SecretWordPage() {
  const { questionId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session");

  const [data, setData]     = useState(null);
  const [status, setStatus] = useState("loading"); // "loading" | "ready" | "error"
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  // "idle" | "sending" | "done" | "error"
  const [readyState, setReadyState] = useState("idle");

  useEffect(() => {
    setStatus("loading");
    setImgError(false);
    setImgLoaded(false);
    setReadyState("idle");
    axios.get(`${API}/secret/${questionId}`)
      .then(({ data: d }) => { setData(d); setStatus("ready"); })
      .catch(() => setStatus("error"));
  }, [questionId]);

  /* ── Loading ── */
  if (status === "loading") return (
    <div style={{ minHeight:"100svh", background:"#FFF8E6", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <p style={{ color:"#5B0E14", fontFamily:"'Tajawal','Cairo',sans-serif", fontSize:"1.2rem", fontWeight:700 }}>
        جاري التحميل...
      </p>
    </div>
  );

  /* ── Error ── */
  if (status === "error") return (
    <div style={{ minHeight:"100svh", background:"#FFF8E6", display:"flex", alignItems:"center", justifyContent:"center", padding:"24px" }}>
      <div style={{ textAlign:"center", fontFamily:"'Tajawal','Cairo',sans-serif" }}>
        <div style={{ fontSize:"3rem", marginBottom:"12px" }}>❌</div>
        <p style={{ color:"#5B0E14", fontSize:"1.4rem", fontWeight:800, margin:0 }}>رابط غير صحيح</p>
        <p style={{ color:"#5B0E14", opacity:0.55, fontSize:"1rem", marginTop:"6px" }}>هذا الرابط لا يعمل</p>
      </div>
    </div>
  );

  const diffLabel = DIFFICULTY_LABEL[data.difficulty] ?? "—";
  const actorGuide = getActorGuide(data.actor_guide);

  /* ── Ready ── */
  return (
    <div style={{
      minHeight: "100svh",
      background: "#FFF8E6",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "32px 20px 40px",
      fontFamily: "'Tajawal','Cairo',sans-serif",
      direction: "rtl",
    }}>

      {/* Brand header */}
      <div style={{ marginBottom:"28px", textAlign:"center" }}>
        <div style={{ color:"#5B0E14", fontSize:"2rem", fontWeight:900, letterSpacing:"-0.5px", lineHeight:1 }}>
          حُجّة
        </div>
        <div style={{ color:"#5B0E14", opacity:0.5, fontSize:"0.85rem", fontWeight:600, marginTop:"4px" }}>
          تمثيل صامت — {diffLabel}
        </div>
      </div>

      {/* Word card */}
      <div style={{
        width: "100%",
        maxWidth: "380px",
        background: "#F1E194",
        borderRadius: "20px",
        padding: "32px 24px",
        textAlign: "center",
        boxShadow: "0 4px 24px rgba(91,14,20,0.12)",
        marginBottom: "20px",
      }}>
        <div style={{ color:"#5B0E14", opacity:0.6, fontSize:"0.78rem", fontWeight:700, marginBottom:"10px", letterSpacing:"0.06em" }}>
          العنوان السري
        </div>
        <div
          data-testid="secret-word"
          dir="auto"
          style={{
            color: "#5B0E14",
            fontSize: "clamp(1.45rem, 7vw, 2.4rem)",
            fontWeight: 900,
            lineHeight: 1.35,
            unicodeBidi: "plaintext",
            overflowWrap: "anywhere",
          }}
        >
          {data.word}
        </div>
      </div>

      {/* Answer image */}
      {data.answer_image_url && (
        <div style={{
          width: "100%",
          maxWidth: "380px",
          marginBottom: "20px",
          borderRadius: "16px",
          overflow: "hidden",
          background: "#FFF8E6",
          border: "1.5px solid rgba(91,14,20,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80px",
          position: "relative",
        }}>
          {!imgLoaded && !imgError && (
            <div data-testid="answer-image-loading" style={{ color:"rgba(91,14,20,0.55)", fontSize:"0.82rem", fontWeight:700, padding:"28px 16px" }}>
              جارٍ تحميل الصورة...
            </div>
          )}
          {imgError ? (
            <div data-testid="answer-image-fallback" style={{ color:"rgba(91,14,20,0.55)", fontSize:"0.82rem", fontWeight:700, padding:"22px 16px", textAlign:"center" }}>
              تعذّر تحميل صورة الحالة
            </div>
          ) : (
            <img
              src={data.answer_image_url}
              alt={data.word}
              data-testid="answer-image"
              loading="eager"
              decoding="async"
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              style={{
                display: imgLoaded ? "block" : "none",
                width: "100%",
                maxHeight: "240px",
                objectFit: "contain",
              }}
            />
          )}
        </div>
      )}

      {/* Optional actor-only guide. Generic charades instructions are omitted. */}
      {actorGuide && (
        <div data-testid="actor-guide" style={{
          width: "100%",
          maxWidth: "380px",
          background: "#FFFDF5",
          border: "1.5px solid rgba(91,14,20,0.14)",
          borderRadius: "16px",
          padding: "18px 20px",
          marginBottom: "20px",
          boxShadow: "0 3px 16px rgba(91,14,20,0.07)",
        }}>
          <div style={{ color:"#5B0E14", fontWeight:900, fontSize:"0.9rem", marginBottom:"7px" }}>
            💡 أفكار تساعدك بالتمثيل
          </div>
          <div dir="auto" style={{ color:"#5B0E14", fontSize:"0.96rem", fontWeight:650, lineHeight:1.75, unicodeBidi:"plaintext", overflowWrap:"anywhere" }}>
            {actorGuide}
          </div>
        </div>
      )}

      {/* Instructions card */}
      <div style={{
        width: "100%",
        maxWidth: "380px",
        background: "#5B0E14",
        borderRadius: "16px",
        padding: "20px 22px",
        marginBottom: "28px",
      }}>
        <div style={{ color:"#F1E194", fontWeight:800, fontSize:"0.9rem", marginBottom:"12px" }}>
          قواعد التمثيل الصامت
        </div>
        <ul style={{ margin:0, padding:0, listStyle:"none", display:"flex", flexDirection:"column", gap:"8px" }}>
          {[
            "مثّل العنوان لفريقك دون أي كلام",
            "يمنع نطق أي كلمة أو إصدار تلميحات صوتية",
            "يمنع تهجئة العنوان أو تحريك الشفاه بالكلمات",
            "استخدم الإيماءات ولغة الجسد فقط",
          ].map((rule, i) => (
            <li key={i} style={{ display:"flex", alignItems:"flex-start", gap:"8px" }}>
              <span style={{ color:"#F1E194", fontWeight:900, flexShrink:0, marginTop:"1px" }}>•</span>
              <span style={{ color:"#FFF8E6", fontSize:"0.875rem", lineHeight:1.5 }}>{rule}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Ready button */}
      <button
        type="button"
        data-testid="ready-btn"
        disabled={readyState === "sending" || readyState === "done"}
        onClick={async () => {
          if (readyState === "sending" || readyState === "done") return;
          if (!sessionId) {
            setReadyState("error");
            return;
          }
          setReadyState("sending");
          try {
            await axios.post(`${API}/secret/${questionId}/ready?session_id=${sessionId}`);
            setReadyState("done");
          } catch {
            setReadyState("error");
          }
        }}
        style={{
          width: "100%",
          maxWidth: "380px",
          padding: "18px",
          background:
            readyState === "done"  ? "#1a5c35" :
            readyState === "error" ? "#7a1520" :
            readyState === "sending" ? "#3d3d3d" :
            "#5B0E14",
          color: "#F1E194",
          border: "2px solid rgba(241,225,148,0.30)",
          borderRadius: "14px",
          fontSize: "1.2rem",
          fontWeight: 900,
          fontFamily: "'Tajawal','Cairo',sans-serif",
          cursor: (readyState === "sending" || readyState === "done") ? "not-allowed" : "pointer",
          boxShadow: "0 4px 20px rgba(91,14,20,0.30)",
          letterSpacing: "0.02em",
          transition: "background 0.25s",
          opacity: readyState === "sending" ? 0.80 : 1,
          position: "relative",
          zIndex: 10,
          WebkitTapHighlightColor: "rgba(241,225,148,0.20)",
          touchAction: "manipulation",
        }}
      >
        {readyState === "done"    ? "✓ تم — ابدأ التمثيل!" :
         readyState === "sending" ? "⏳ جاري..." :
         readyState === "error"   ? "⟳ حاول مجدداً" :
         "جاهز ✓"}
      </button>
      {readyState === "error" && (
        <div style={{ marginTop:"10px", color:"#7a1520", fontSize:"0.82rem", fontWeight:700, textAlign:"center" }}>
          فشل الاتصال — اضغط مجدداً أو أخبر المضيف
        </div>
      )}
      {!sessionId && readyState !== "done" && (
        <div style={{ marginTop:"10px", color:"#5B0E14", opacity:0.45, fontSize:"0.72rem", textAlign:"center" }}>
          افتح الرابط عبر مسح الباركود لتفعيل الزر
        </div>
      )}

      <div style={{ marginTop:"20px", color:"#5B0E14", opacity:0.3, fontSize:"0.75rem" }}>
        لعبة حُجّة – تمثيل صامت
      </div>
    </div>
  );
}
